import React from "react"
import ReactDOM from "react-dom"

import { Button } from "../button/Button"
import { Text } from "../text/Text"

export interface CameraPopupProps {
  /** Callback when user confirms the captured image. Receives the raw data URL. */
  onConfirm: (dataUrl: string) => void
  /** Callback when user cancels capture. */
  onCancel: () => void
  /** Callback when camera acquisition fails. */
  onError: () => void
}

export type CameraPopupPhase = "viewfinder" | "preview"

export interface CameraPopupState {
  /** Current phase of the popup UI. */
  phase: CameraPopupPhase
  /** Active media stream for the viewfinder. */
  mediaStream: MediaStream | null
  /** Data URL of the captured frame (set when phase is "preview"). */
  capturedDataUrl: string | null
}

/**
 * Fullscreen camera popup overlay for WebRTC capture.
 *
 * Provides a Capture → Preview → Confirm/Retake flow.
 * Renders as a portal into document.body to sit above all page content.
 *
 * This is an internal component — not exported from the public API.
 */
export class CameraPopup extends React.PureComponent<CameraPopupProps, CameraPopupState> {
  private readonly videoRef = React.createRef<HTMLVideoElement>()

  public constructor(props: CameraPopupProps) {
    super(props)
    this.state = {
      phase: "viewfinder",
      mediaStream: null,
      capturedDataUrl: null,
    }
  }

  public componentDidMount(): void {
    this.acquireStream()
  }

  public componentWillUnmount(): void {
    this.stopStream()
  }

  /**
   * Request camera via getUserMedia with facingMode: "environment" preference.
   * Falls back to any camera ({ video: true }) if environment mode fails.
   * On complete failure, calls onError to notify the parent.
   */
  private async acquireStream(): Promise<void> {
    try {
      let stream: MediaStream
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "environment" },
        })
      } catch (envErr) {
        console.warn("Rear camera unavailable, trying any camera:", envErr)
        stream = await navigator.mediaDevices.getUserMedia({ video: true })
      }
      this.setState({ mediaStream: stream, phase: "viewfinder" }, () => {
        if (this.videoRef.current) {
          this.videoRef.current.srcObject = stream
        }
      })
    } catch (err) {
      console.error("Failed to acquire camera stream:", err)
      this.props.onError()
    }
  }

  /**
   * Stop all tracks on the active MediaStream and clear the reference.
   */
  private stopStream(): void {
    if (this.state.mediaStream) {
      this.state.mediaStream.getTracks().forEach((track) => track.stop())
      this.setState({ mediaStream: null })
    }
  }

  /**
   * Capture the current video frame to a canvas, store the data URL,
   * stop the stream, and transition to the preview phase.
   */
  private handleCapture = () => {
    const video = this.videoRef.current
    if (!video) {
      return
    }

    const canvas = document.createElement("canvas")
    canvas.width = video.videoWidth
    canvas.height = video.videoHeight
    const ctx = canvas.getContext("2d")
    if (!ctx) {
      console.error("Failed to get canvas 2D context")
      return
    }

    ctx.drawImage(video, 0, 0)
    const dataUrl = canvas.toDataURL("image/jpeg", 1.0)

    this.stopStream()
    this.setState({
      phase: "preview",
      capturedDataUrl: dataUrl,
    })
  }

  /**
   * Discard the captured frame and reacquire the camera stream
   * to return to the viewfinder phase.
   */
  private handleRetake = () => {
    this.setState({ capturedDataUrl: null })
    this.acquireStream()
  }

  /**
   * Confirm the captured image by passing the data URL to the parent.
   */
  private handleConfirm = () => {
    if (this.state.capturedDataUrl) {
      this.props.onConfirm(this.state.capturedDataUrl)
    }
  }

  /**
   * Cancel capture: stop the stream and notify the parent.
   */
  private handleCancel = () => {
    this.stopStream()
    this.props.onCancel()
  }

  public render() {
    const { phase, capturedDataUrl } = this.state

    const overlay = (
      <div className="camera-popup">
        <div className="camera-popup__content">
          {phase === "viewfinder" ? (
            <video
              ref={this.videoRef}
              className="camera-popup__video"
              autoPlay={true}
              playsInline={true}
              muted={true}
            />
          ) : (
            <img
              className="camera-popup__preview"
              src={capturedDataUrl || undefined}
              alt="Captured preview"
            />
          )}
        </div>
        <div className="camera-popup__controls">
          {phase === "viewfinder" ? (
            <>
              <Button variant="primary" onClick={this.handleCapture}>
                <Text>Capture</Text>
              </Button>
              <Button variant="primary" onClick={this.handleCancel}>
                <Text>Cancel</Text>
              </Button>
            </>
          ) : (
            <>
              <Button variant="secondary" onClick={this.handleConfirm}>
                <Text>Confirm</Text>
              </Button>
              <Button variant="secondary" onClick={this.handleRetake}>
                <Text>Retake</Text>
              </Button>
            </>
          )}
        </div>
      </div>
    )

    return ReactDOM.createPortal(overlay, document.body)
  }
}
